import { Lightning, ChartLineUp, Target, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { fmtNum, timeAgo } from "@/api";

function ProbRing({ score, dir }) {
  const size = 44;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const dash = c * pct;
  const isShort = dir === "short";
  const color = isShort
    ? score >= 75 ? "#FF3B30" : score >= 60 ? "#F5C518" : "#A1A1AA"
    : score >= 75 ? "#00FF66" : score >= 60 ? "#F5C518" : "#A1A1AA";
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`}
          strokeDashoffset={c / 4}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute font-mono text-[11px] font-semibold" style={{ color }}>{Math.round(score)}</span>
    </div>
  );
}

const LONG_CRITERIA = [
  "htf_bull_bias",
  "major_trend_flip",
  "weekly_structure_break",
  "ml_lorentzian",
  "ema_stack",
  "supertrend_up",
  "rsi_bounce",
  "unfilled_fvg",
  "volume_spike",
  "obv_rising",
  "bull_divergence",
  "double_bottom",
  "above_basis",
];
const SHORT_CRITERIA = [
  "htf_bear_bias",
  "major_trend_flip",
  "weekly_structure_break",
  "ml_lorentzian",
  "ema_stack",
  "supertrend_down",
  "rsi_rejection",
  "unfilled_fvg",
  "volume_spike",
  "obv_falling",
  "bear_divergence",
  "double_top",
  "below_basis",
];

export function SignalRow({ signal, onSelect, isNew }) {
  const isShort = signal.direction === "short";
  const lev = signal.recommended_leverage;
  const levColor = lev >= 75 ? (isShort ? "#FF3B30" : "#00FF66") : lev >= 50 ? "#F5C518" : "#FFFFFF";
  const accent = isShort ? "#FF3B30" : "#00FF66";
  const orderList = isShort ? SHORT_CRITERIA : LONG_CRITERIA;

  return (
    <div
      data-testid={`signal-row-${signal.symbol}-${signal.direction || "long"}`}
      onClick={() => onSelect?.(signal)}
      className={`group cursor-pointer border-b border-white/5 hover:-translate-y-[1px] transition-all bg-[#101010] hover:bg-[#141414] px-3 py-3 ${isNew ? "row-flash" : ""}`}
      style={{ borderLeft: `2px solid ${accent}` }}
    >
      <div className="flex items-center gap-3">
        <ProbRing score={signal.score} dir={signal.direction} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-white text-[13px] tracking-wide">{signal.symbol}</span>
            <span
              className="font-mono text-[9px] font-bold px-1.5 py-[1px] border tracking-widest uppercase flex items-center gap-0.5"
              style={{ color: accent, borderColor: `${accent}55`, background: `${accent}0f` }}
            >
              {isShort ? <ArrowDown size={9} weight="bold" /> : <ArrowUp size={9} weight="bold" />}
              {isShort ? "Short" : "Long"}
            </span>
            <span
              className="font-mono text-[9px] px-1.5 py-[1px] border border-white/15 text-white/70 tracking-widest uppercase"
              title={`${signal.mode || "scalp"} mode`}
            >
              {signal.mode || "scalp"} · {signal.timeframe || "5m"}
            </span>
            {signal.criteria?.major_trend_flip?.hit && (
              <span
                className="font-mono text-[9px] font-bold px-1.5 py-[1px] border tracking-widest uppercase"
                style={{ color: "#F5C518", borderColor: "#F5C51888", background: "#F5C51820" }}
                title="Higher-timeframe Supertrend just flipped — major trend change"
              >
                ★ TREND FLIP
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 font-mono text-[11px]">
            <span className="text-white/60">@</span>
            <span className="text-white">{fmtNum(signal.entry)}</span>
            <span className="text-white/30">·</span>
            <span className="text-white/50">{timeAgo(signal.created_at)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div
            className="font-mono text-[11px] font-bold px-2 py-[3px] border"
            style={{ color: levColor, borderColor: `${levColor}40`, background: `${levColor}12` }}
          >
            {lev}× LEV
          </div>
          <div className="font-mono text-[10px] text-white/40">
            ATR {signal.atr_pct}%
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-2 font-mono text-[10px]">
        <div className="border border-white/5 bg-[#0A0A0A] px-2 py-1">
          <div className="text-white/40 text-[9px] uppercase tracking-wider">SL</div>
          <div className="text-[#FF3B30]">{fmtNum(signal.stop_loss)}</div>
        </div>
        <div className="border border-white/5 bg-[#0A0A0A] px-2 py-1">
          <div className="text-white/40 text-[9px] uppercase tracking-wider">TP1</div>
          <div style={{ color: accent }}>{fmtNum(signal.take_profit_1)}</div>
        </div>
        <div className="border border-white/5 bg-[#0A0A0A] px-2 py-1">
          <div className="text-white/40 text-[9px] uppercase tracking-wider">TP2</div>
          <div style={{ color: accent }}>{fmtNum(signal.take_profit_2)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mt-2">
        {orderList.map((k) => {
          const c = signal.criteria?.[k];
          if (!c) return null;
          const hit = c.hit;
          return (
            <span
              key={k}
              className={`font-mono text-[9px] px-1.5 py-[2px] border tracking-wider uppercase ${
                hit
                  ? "border-white/25 bg-white/5"
                  : "text-white/25 border-white/5 line-through"
              }`}
              style={hit ? { color: accent, borderColor: `${accent}55`, background: `${accent}0d` } : {}}
              title={c.label}
            >
              {c.label}
            </span>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-2 text-[10px] font-mono text-white/40">
        <span className={signal.weex_available ? "text-white" : ""}>WEEX {signal.weex_available ? "✓" : "×"}</span>
        <span className={signal.blofin_available ? "text-white" : ""}>BLOFIN {signal.blofin_available ? "✓" : "×"}</span>
        <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition" style={{ color: accent }}>
          <Target size={11} /> Analyze
        </span>
      </div>
    </div>
  );
}

export function SignalsFeed({ signals, isLoading, onSelect, filter, setFilter, dirFilter, setDirFilter, modeFilter, setModeFilter }) {
  return (
    <div data-testid="signals-feed" className="border border-white/10 bg-[#0A0A0A] flex flex-col h-full">
      <div className="px-4 py-3 border-b border-white/10 bg-[#0f0f0f] flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Lightning size={16} weight="fill" className="text-[#00FF66]" />
          <h2 className="font-display font-bold text-sm tracking-widest uppercase text-white">Live Signals</h2>
          <div className="ml-auto font-mono text-[11px] text-white/50">
            <select
              data-testid="filter-min-score"
              value={filter}
              onChange={(e) => setFilter(Number(e.target.value))}
              className="bg-black border border-white/10 rounded-none px-2 py-1 text-white font-mono text-[11px]"
            >
              <option value={0}>All scores</option>
              <option value={60}>Score ≥ 60</option>
              <option value={70}>Score ≥ 70</option>
              <option value={80}>Score ≥ 80</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-white/10" data-testid="mode-filter">
            {[
              { k: "all", label: "All" },
              { k: "scalp", label: "Scalp 5m" },
              { k: "swing", label: "Swing 1h" },
              { k: "position", label: "Position 4h" },
              { k: "macro", label: "Macro 1D" },
            ].map((m) => (
              <button
                key={m.k}
                data-testid={`mode-filter-${m.k}`}
                onClick={() => setModeFilter(m.k)}
                className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                  modeFilter === m.k ? "bg-white text-black" : "text-white/60 hover:bg-white/5"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="flex border border-white/10" data-testid="dir-filter">
            {["all", "long", "short"].map((d) => (
              <button
                key={d}
                data-testid={`dir-filter-${d}`}
                onClick={() => setDirFilter(d)}
                className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                  dirFilter === d
                    ? d === "long"
                      ? "bg-[#00FF66] text-black"
                      : d === "short"
                      ? "bg-[#FF3B30] text-black"
                      : "bg-white text-black"
                    : "text-white/60 hover:bg-white/5"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="p-6 text-white/40 font-mono text-xs">
            Booting scanner<span className="terminal-cycle">...</span>
          </div>
        )}
        {!isLoading && (!signals || signals.length === 0) && (
          <div className="p-6 flex flex-col items-center gap-2 text-center">
            <ChartLineUp size={32} className="text-white/20" />
            <div className="font-mono text-xs text-white/50">
              Watching market. No high-conviction triggers matching this filter right now.
            </div>
            <div className="font-mono text-[10px] text-white/30">Signals require ≥5 confluences and HTF bias alignment.</div>
          </div>
        )}
        {signals?.map((s, idx) => (
          <SignalRow key={`${s.symbol}-${s.direction}-${s.created_at}`} signal={s} onSelect={onSelect} isNew={idx === 0} />
        ))}
      </div>
    </div>
  );
}
